import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  Box,
  Button,
  Divider,
  Flex,
  Radio,
  RadioGroup,
  SelectField,
  spaces,
  Text,
  TextAreaField,
  TextInputField,
} from '@electricjs/arc';
import {
  CurrencyCodeEnum,
  PermissionEnum,
} from '@electricjs/core_entity-client';

import { getAssetSchema } from '@/components/Assets/AssetSchema';
import {
  AssetConditionOption,
  AssetStatusOption,
  getAssetStatusOptions,
  LocationOption,
  ProductTypeOption,
  SiteOption,
  transformAssetConditionsToOptions,
  transformLocationsToOptions,
  transformProductTypesToOptions,
  transformSitesToOptions,
} from '@/components/Assets/helpers/transformOptions';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import { EmployeeOption } from '@/components/People/EmployeeGroups/NewEmployeeGroupForm';
import { transformEmployeesToOptions } from '@/components/People/transformOptions';
import { ELECTRIC_WAREHOUSE_SITE_NAME } from '@/constants/assets';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useIsAdpWfnCanadaConnectedOrg } from '@/hooks/useIsAdpWfnCanadaConnectedOrg';
import { useOrganizationEmployees } from '@/hooks/useOrganizationEmployees';
import { useOrganizationProductTypes } from '@/hooks/useOrganizationProductTypes';
import { useUserHasOrganizationPermission } from '@/hooks/useUserHasOrganizationPermission';
import {
  useCreateAssetsMutation,
  useGetOrganizationSitesQuery,
  useGetSiteLocationsQuery,
} from '@/redux/slices/assetApiSlice';

import SiteModal from './SiteModal';
import { SiteSelection } from '@/components/Assets/Sites';
import { PriceField } from 'common/PriceField';
import { DateField } from 'common/DateField';

export type AssetFormData = {
  name: string;
  productType: ProductTypeOption | null;
  customAssetId: string;
  site?: SiteOption | null;
  purchaseDate?: Date;
  purchasePrice?: string | null;
  purchasePriceCurrencyCode: CurrencyCodeEnum;
  purchasedFrom: string;
  brand: string;
  model: string;
  serialNumber: string;
  notes: string;
  internalNotes: string;
  assignedTo?: EmployeeOption | null;
  status: AssetStatusOption | null;
  condition: AssetConditionOption | null;
  imageUrl: string;
  warrantyExpirationDate?: Date;
  location?: LocationOption | null;
};

export const assetDefaultValues: AssetFormData = {
  name: '',
  productType: null,
  customAssetId: '',
  assignedTo: null,
  purchaseDate: undefined,
  purchasePrice: null,
  purchasePriceCurrencyCode: 'USD',
  purchasedFrom: '',
  brand: '',
  model: '',
  serialNumber: '',
  notes: '',
  internalNotes: '',
  site: null,
  status: null,
  condition: null,
  imageUrl: '',
  warrantyExpirationDate: undefined,
  location: null,
};

// @TODO: move data fetch to parent component to show an error message if the fetch fails
const NewAssetForm = () => {
  const { t, i18n } = useTranslation('assets');
  const isAdpWfnCanada = useIsAdpWfnCanadaConnectedOrg();
  const { showErrorToast, showSuccessToast } = useGlobalUI();
  const { openModal, closeModal } = useGlobalUI();
  const navigate = useNavigate();
  const conditionOptions = transformAssetConditionsToOptions(t);
  const statusOptions = getAssetStatusOptions();
  const canAddInternalNotes = useUserHasOrganizationPermission(
    PermissionEnum.Warehousingcreate
  );
  const [hasAssetAssignment, setHasAssetAssignment] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { isValid },
    watch,
    reset,
    setError,
    clearErrors,
    setValue,
  } = useForm<AssetFormData>({
    resolver: yupResolver(getAssetSchema(t)),
    mode: 'onTouched',
    defaultValues: assetDefaultValues,
  });
  const siteValue = useWatch({ name: 'site', control });

  const { productTypes, isFetching } = useOrganizationProductTypes();
  const productTypeOptions = transformProductTypesToOptions(productTypes);

  const organizationId = useGetOrganizationId();

  const { data: sites, isFetching: isSitesFetching } =
    useGetOrganizationSitesQuery({
      organizationId,
    });

  const canSetWarehouseAsSite = useUserHasOrganizationPermission(
    PermissionEnum.Warehousingupdate
  );
  const validSites =
    sites?.filter(s => canSetWarehouseAsSite || s.organizationId) ?? [];
  const siteOptions = sites ? transformSitesToOptions(validSites) : [];

  const canReadAssetLocation = useUserHasOrganizationPermission(
    PermissionEnum.Assetslocationread
  );
  const canUpdateAssetLocation = useUserHasOrganizationPermission(
    PermissionEnum.Assetslocationupdate
  );

  const warehouseSite = sites?.find(
    site => site.name === ELECTRIC_WAREHOUSE_SITE_NAME && !site.organizationId
  );
  const isWarehouseSelected =
    !!siteValue && siteValue?.value === warehouseSite?.id;

  const canUseLocations =
    isWarehouseSelected && canReadAssetLocation && canUpdateAssetLocation;

  const { data: locations, isFetching: isFetchingLocations } =
    useGetSiteLocationsQuery(siteValue?.value ?? '', {
      skip: !canUseLocations,
    });

  const locationOptions = useMemo(
    () => transformLocationsToOptions(locations || []),
    [locations]
  );

  const employees = useOrganizationEmployees();
  const employeeOptions = transformEmployeesToOptions(
    employees,
    i18n.language,
    isAdpWfnCanada
  );

  const watched = watch();

  useEffect(() => {
    if (hasAssetAssignment || watched.site?.value) {
      clearErrors('assignedTo');
      clearErrors('site');
    }
  }, [clearErrors, hasAssetAssignment, watched.site?.value]);

  useEffect(() => {
    if (!hasAssetAssignment) setValue('assignedTo', null);
  }, [hasAssetAssignment, setValue]);

  // Clear location value when the Warehouse is not selected as the site
  useEffect(() => {
    if (!isWarehouseSelected) {
      setValue('location', null);
    }
  }, [isWarehouseSelected, setValue]);

  const [createAssets, { isLoading: isCreateAssetsLoading }] =
    useCreateAssetsMutation();

  const onSubmit = (data: AssetFormData) => {
    if (!watched.assignedTo?.value && hasAssetAssignment) {
      const errorText = t('newAssetForm.assignedToError');
      setError('assignedTo', { message: errorText });
      return;
    }
    if (!watched.site?.value && !hasAssetAssignment) {
      const errorText = t('newAssetForm.siteError');
      setError('site', { message: errorText });
      return;
    }

    const productTypeId = (
      productTypes?.find(
        productType => productType.id === data.productType?.value
      )?.id ?? ''
    ).toString();

    createAssets({
      organizationId: organizationId,
      assets: [
        {
          name: data.name,
          productTypeId,
          customAssetId: data.customAssetId,
          assignedTo: data.assignedTo?.value,
          purchaseDate: data.purchaseDate,
          purchasePrice: data.purchasePrice
            ? Number(data.purchasePrice)
            : undefined,
          purchasePriceCurrencyCode: data.purchasePrice
            ? data.purchasePriceCurrencyCode
            : undefined,
          purchasedFromUrl: data.purchasedFrom,
          brand: data.brand,
          model: data.model,
          serialNumber: data.serialNumber,
          notes: data.notes,
          internalNotes: data.internalNotes,
          siteId: data.site?.value,
          locationId: data.location?.value,
          status: data.status?.value,
          condition: data.condition?.value,
          imageUrl: data.imageUrl,
          warrantyExpirationDate: data.warrantyExpirationDate,
        },
      ],
    })
      .unwrap()
      .catch(error => {
        console.error('Error while adding asset: ', error);
        showErrorToast({
          id: 'add-asset-error-toast',
          message: t('newAssetForm.errorMessage'),
        });
        return Promise.reject(error);
      })
      .then(() => {
        showSuccessToast({
          id: 'add-asset-success-toast',
          message: t('newAssetForm.successMessage'),
        });
        reset();
        setHasAssetAssignment(false);
      });
  };

  const handleRadioClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasAssetAssignment(e.target.value === 'true');
  };

  return (
    <Box>
      <Flex vertical alignItems="left" my="1.6rem" rowGap="1rem">
        <Text variant="heading-3">{t('newAssetForm.itemDetails')}</Text>
      </Flex>
      <form>
        <Flex columnGap="2rem">
          <TextInputField
            id="asset-name"
            name="name"
            control={control}
            label={t('newAssetForm.name')}
            required
          />
          <TextInputField
            id="asset-custom-id"
            name="customAssetId"
            control={control}
            label={t('newAssetForm.assetId')}
          />
        </Flex>
        <Flex columnGap="2rem">
          <SelectField
            id="product-type"
            key={watched?.productType?.value}
            name="productType"
            control={control}
            label={t('newAssetForm.assetType')}
            options={productTypeOptions}
            required
            isSearchable
            placeholder={t('newAssetForm.assetTypePlaceholder')}
            isLoading={isFetching}
          />
          <SelectField
            id="asset-status"
            key={watched?.status?.value}
            control={control}
            name="status"
            label={t('newAssetForm.status')}
            placeholder={t('newAssetForm.statusPlaceholder')}
            options={statusOptions}
            isClearable
            isSearchable
          />
        </Flex>
        <Flex columnGap="2rem">
          <DateField
            id="asset-purchase-date"
            key={watched?.purchaseDate?.toISOString()}
            name="purchaseDate"
            control={control}
            label={t('newAssetForm.purchaseDate')}
            placeholder={t('newAssetForm.purchaseDatePlaceholder')}
            minDate={null} // No min date
          />
          <PriceField
            id="asset-purchase-price"
            name="purchasePrice" // using the "purchasePrice" property to handle validation schema
            control={control}
            // manually overriding value/onChange, as the Asset form uses separated fields for price and currency
            overrides={{
              value: {
                amount: watched.purchasePrice ?? '',
                currencyCode: watched.purchasePriceCurrencyCode,
              },
              onChange: price => {
                setValue('purchasePrice', price?.amount, {
                  shouldValidate: true,
                });
                setValue('purchasePriceCurrencyCode', price.currencyCode, {
                  shouldValidate: true,
                });
              },
            }}
            label={t('newAssetForm.purchasePrice')}
          />
          <TextInputField
            id="asset-purchased-from"
            name="purchasedFrom"
            control={control}
            label={t('newAssetForm.purchasedFrom')}
            placeholder={t('newAssetForm.purchasedFromPlaceholder')}
          />
        </Flex>
        <Flex columnGap="2rem">
          <TextInputField
            id="asset-brand"
            name="brand"
            control={control}
            label={t('newAssetForm.brand')}
          />
          <TextInputField
            id="asset-model"
            name="model"
            control={control}
            label={t('newAssetForm.model')}
          />
          <TextInputField
            id="asset-serial-number"
            name="serialNumber"
            control={control}
            label={t('newAssetForm.serialNumber')}
          />
        </Flex>
        <Flex columnGap="2rem">
          <SelectField
            id="asset-condition"
            key={watched?.condition?.value}
            name="condition"
            control={control}
            label={t('newAssetForm.condition')}
            options={conditionOptions}
            value={watched.condition}
            placeholder={t('newAssetForm.conditionPlaceholder')}
            isClearable
            isSearchable
          />
          <DateField
            id="warranty-expiration-date"
            key={watched?.warrantyExpirationDate?.toISOString()}
            name="warrantyExpirationDate"
            control={control}
            label={t('newAssetForm.warrantyExpiration')}
            placeholder={t('newAssetForm.warrantyExpirationPlaceholder')}
            isClearable
            minDate={null} // No min date
          />
        </Flex>
        <Flex width="67%" hAlignContent="center" vertical>
          <TextAreaField
            id="asset-notes"
            name="notes"
            control={control}
            label={t('newAssetForm.notes')}
            placeholder={t('newAssetForm.notesPlaceholder')}
            helperText={`${watched.notes?.length}/1000`}
            helperTextJustifyContent="flex-end"
            maxLength={1000}
          />
        </Flex>
        {canAddInternalNotes && (
          <Flex width="67%" hAlignContent="center" vertical>
            <TextAreaField
              id="asset-internal-notes"
              name="internalNotes"
              control={control}
              label={t('newAssetForm.internalNotes')}
              placeholder={t('newAssetForm.internalNotesPlaceholder')}
              helperText={`${watched.internalNotes?.length}/1000`}
              helperTextJustifyContent="flex-end"
              maxLength={1000}
            />
          </Flex>
        )}
        <Divider />
        <Text mt={spaces.space_m}>{t('newAssetForm.assignmentQuestion')}</Text>
        <RadioGroup stack p={spaces.space_xl}>
          <Radio
            id="asset-not-assigned"
            value="false"
            onChange={handleRadioClick}
            checked={!hasAssetAssignment}>
            {t('newAssetForm.notAssigned')}
          </Radio>
          <Radio
            id="asset-assigned"
            value="true"
            onChange={handleRadioClick}
            checked={hasAssetAssignment}>
            {t('newAssetForm.assigned')}
          </Radio>
        </RadioGroup>
        {hasAssetAssignment && (
          <SelectField
            id="asset-assigned-to"
            key={watched?.assignedTo?.value}
            name="assignedTo"
            control={control}
            label={t('newAssetForm.assignedTo')}
            options={employeeOptions}
            placeholder={t('newAssetForm.assignedToPlaceholder')}
            isClearable
            isSearchable
            required={hasAssetAssignment}
          />
        )}
        <SiteSelection
          key={watched?.site?.value}
          control={control}
          siteOptions={siteOptions}
          isSitesFetching={isSitesFetching}
          required={!hasAssetAssignment}
          canUseLocations={canUseLocations}
          locationOptions={locationOptions}
          isFetchingLocations={isFetchingLocations}
          onAddSite={() =>
            openModal(<SiteModal hideModal={() => closeModal()} />)
          }
        />
        <Flex justifyContent="flex-end" alignSelf="flex-end" columnGap="2rem">
          <Button
            id="asset-button-cancel"
            variant="text"
            onClick={() => navigate('/assets')}>
            {t('newAssetForm.cancel')}
          </Button>
          <Button
            id="asset-button-add"
            onClick={handleSubmit(onSubmit)}
            disabled={!isValid}
            loading={isCreateAssetsLoading}>
            {t('newAssetForm.addAsset')}
          </Button>
        </Flex>
      </form>
    </Box>
  );
};

export default NewAssetForm;
